import Feature from "ol/Feature";
import { Geometry } from "ol/geom";
import { Coordinate } from "ol/coordinate";
import { positionsFromFeature } from "../../utils/feature";
import { ModifyEvent } from "ol/interaction/Modify";
import { TranslateEvent } from "ol/interaction/Translate";
import { FeatureCollection, FeatureFromGeojson } from "../FeaturesStore";
import { MakeGeojsonShape, makeGeojsonShape } from "../../utils/makeGeojsonShape";

/** 이동된 feature의 GeoJSON 기본 속성 (geometry 이외 메타데이터) */
const TRANSLATED_FEATURE_DEFAULTS = {
  title: "unknown",
  comment: "",
  issueDegree: "심각도",
  issue: "이슈 사항",
  issueGrade: 1,
} as const;

/**
 * 기하 타입과 새 좌표로 GeoJSON geometry 객체를 구성합니다.
 * Point는 단일 좌표, 나머지는 좌표 배열을 사용합니다.
 */
function buildUpdatedGeometry(
  type: string,
  newPosition: Coordinate | Coordinate[]
): { type: string; coordinates: Coordinate | Coordinate[] } {
  if (type === "Point") {
    return { type: "Point", coordinates: newPosition as Coordinate };
  }
  return { type, coordinates: newPosition as Coordinate[] };
}

/**
 * 중첩 여부와 무관하게 positions 배열에서 첫 번째 스칼라 좌표를 추출합니다.
 * - 2D 배열(Coordinate[][]): [0][0]
 * - 1D 배열(Coordinate[]):   [0]
 */
function extractFirstCoordinate(
  positions: Coordinate[] | Coordinate[][]
): Coordinate {
  return Array.isArray(positions[0])
    ? (positions as Coordinate[][])[0][0]
    : (positions as Coordinate[])[0];
}

/**
 * Modify 이벤트에서 업데이트된 GeoJSON feature 데이터를 추출합니다.
 * 변경할 대상이 없으면 null을 반환합니다.
 */
export function resolveModifiedFeatureUpdate(
  event: ModifyEvent,
  geoJson: FeatureCollection,
  getGeoJsonElement: (id: string) => FeatureFromGeojson | undefined,
  isActive: boolean
): { id: string; data: Omit<FeatureFromGeojson, "id"> } | null {
  const targetFeature = event.features.getArray()[0];
  const targetId = String(targetFeature.getId());
  const newPosition = positionsFromFeature(targetFeature as Feature<Geometry>, true) as
    | Coordinate
    | Coordinate[];

  const targetGeoJson =
    geoJson.features.find((f) => f.id === targetId) ||
    getGeoJsonElement(targetId);
  if (!targetGeoJson) return null;

  const newGeometry = buildUpdatedGeometry(targetGeoJson.geometry.type, newPosition);

  return {
    id: targetId,
    data: {
      ...targetGeoJson,
      geometry: newGeometry as MakeGeojsonShape,
      properties: { ...targetGeoJson.properties, isModifying: isActive },
    },
  };
}

/**
 * Translate 이벤트에서 이동된 feature의 GeoJSON 업데이트 데이터를 추출합니다.
 * 유효한 feature가 없으면 null을 반환합니다.
 */
export function resolveTranslatedFeatureUpdate(
  event: TranslateEvent
): { id: string; geoJson: Omit<FeatureFromGeojson, "id"> } | null {
  const targetFeature = event.features.getArray()[0];
  if (!targetFeature) return null;

  const targetId = String(targetFeature.getId());
  if (!targetId || targetId === "undefined") return null;

  const geometry = resolveTranslatedGeometry(targetFeature as Feature<Geometry>);
  const geometryType = targetFeature.getGeometry()?.getType();
  const normalizedType = geometryType?.toLowerCase() || "point";

  return {
    id: targetId,
    geoJson: makeGeojsonShape(geometry as any, {
      ...TRANSLATED_FEATURE_DEFAULTS,
      type: normalizedType,
      color: "blue",
      opacity: 1,
      fontSize: 12,
      original_type: normalizedType,
      isSelected: true,
    }),
  };
}

/**
 * 이동(Translate) 후 OL Feature에서 업데이트된 GeoJSON geometry 객체를 추출합니다.
 */
export function resolveTranslatedGeometry(feature: Feature<Geometry>): {
  type: string;
  coordinates: Coordinate | Coordinate[] | Coordinate[][];
} {
  const geometryType = feature.getGeometry()?.getType();
  const positions = positionsFromFeature(feature, true) as
    | Coordinate[]
    | Coordinate[][];

  switch (geometryType) {
    case "Point":
      return { type: "Point", coordinates: extractFirstCoordinate(positions) };
    case "MultiPoint":
    case "LineString":
      return { type: geometryType, coordinates: positions as Coordinate[] };
    case "Polygon":
      return { type: "Polygon", coordinates: positions as Coordinate[][] };
    default:
      return { type: "Point", coordinates: extractFirstCoordinate(positions) };
  }
}

/** 도(degree) → 라디안 변환 */
export const degreesToRadians = (degrees: number): number =>
  (degrees * Math.PI) / 180;

/** 라디안 → 도(degree) 변환 */
export const radiansToDegrees = (radians: number): number =>
  (radians * 180) / Math.PI;
